import React, {CSSProperties, FC, KeyboardEvent, MouseEventHandler, ReactNode, useCallback, useEffect, useRef, useState} from "react";
import classNames from "classnames";

import {useIsMobile, useIsMounted, useStateRef} from "@pg-mono/hooks";

import {GestureDirection, useSliderTouchEvents} from "../hooks/use_slider_touch_events";
import {SliderGallerySlideNavigation} from "../types/SliderGallerySlideNavigation";
import {getClosestBreakpoint} from "../utils/get_closest_breakpoint";

import {arrow, arrowDisabled, galleryHolder, singleSlide, slider, sliderInnerWrap} from "./SliderGallery.module.css";

export type ISliderTransition = "ease" | "linear" | "ease-in" | "ease-out" | "ease-in-out";
export type IBreakpointSettings = {breakpoint: number; slidesToShow: number; slidesToScroll: number};

interface IProps {
    slides: ReactNode[];
    arrows?: {
        left: React.JSX.Element;
        right: React.JSX.Element;
    };
    preventLazyLoad?: boolean;
    onSlideChange?: (currentSlideIndex: number, nextSlideIndex: number, navigation?: SliderGallerySlideNavigation) => void;
    sliderTransition?: ISliderTransition;
    clonedSlidesCount?: number;
    forceInitialize?: true;
    onResizeDelay?: number; // milliseconds
    initialSlide?: number;
    disableKeyboardEvents?: boolean;
    className?: string;
    sliderWrapperClass?: string;
    slideStyles?: CSSProperties | CSSProperties[];
    preventInfinityScroll?: boolean;
    disableSwipe?: boolean;
    swipeDeadband?: number;

    /// slick-slider-like options:
    slidesToShow?: number;
    slidesToScroll?: number;
    speed?: number;
    breakpoints?: IBreakpointSettings[];
}

const DEFAULT_SPEED = 300;
const DEFAULT_SLIDER_TRANSITION = "ease-out";
const DEFAULT_CLONED_SLIDES_COUNT = 2;
const DEFAULT_SLIDES_TO_SHOW = 1;
const DEFAULT_SLIDES_TO_SCROLL = 1;

// Wait for paint before the next transform step (e.g. instant clone skip, then animated wrap).
const scheduleAfterPaint = (callback: () => void) => {
    requestAnimationFrame(() => {
        requestAnimationFrame(callback);
    });
};

// I am using a lot of refs here to avoid potential issues with stale state being used inside event listeners. Reference to equal state value will always be up to date.

export const SliderGallery: FC<IProps> = (props) => {
    const {
        sliderTransition = DEFAULT_SLIDER_TRANSITION,
        speed = DEFAULT_SPEED,
        clonedSlidesCount = DEFAULT_CLONED_SLIDES_COUNT,
        initialSlide = 0,
        slidesToShow = DEFAULT_SLIDES_TO_SHOW,
        slidesToScroll = DEFAULT_SLIDES_TO_SCROLL,
        disableKeyboardEvents,
        preventInfinityScroll = clonedSlidesCount === 0,
        disableSwipe,
        swipeDeadband,
        onSlideChange
    } = props;

    const galleryHolderRef = useRef<HTMLDivElement | null>(null);
    const gallerySliderRef = useRef<HTMLDivElement | null>(null);
    const isMobile = useIsMobile(true);
    const isMounted = useIsMounted();
    const isSwipeDisabled = Boolean(disableSwipe) || props.slides.length <= 1;

    const [isSliderLoaded, setIsSliderLoaded, isSliderLoadedRef] = useStateRef(false);
    const [translateValue, setTranslateValue] = useState(0);
    const [currentIndex, setCurrentIndex, currentIndexRef] = useStateRef(initialSlide);
    const [breakpointSettings, setBreakpointSettings, breakpointSettingsRef] = useStateRef({
        breakpoint: 0,
        slidesToShow,
        slidesToScroll
    });

    const isTransitioning = useRef(false);
    const slideCountRef = useRef(0);
    const preventAnimationRef = useRef(false);
    const pendingSlideNavigationRef = useRef<SliderGallerySlideNavigation | null>(null);
    const disableSwipeRef = useRef(isSwipeDisabled);

    const slidesListWithClones =
        isSliderLoaded && clonedSlidesCount > 0
            ? [...props.slides.slice(-1 * clonedSlidesCount), ...props.slides, ...props.slides.slice(0, clonedSlidesCount)]
            : props.slides;

    /*
     * initialization, breakpoints and resizing logic
     */

    const setCurrentBreakpointSettings = () => {
        if (!props.breakpoints) return;

        const windowWidth = window.innerWidth;
        setBreakpointSettings(
            getClosestBreakpoint(
                [
                    {
                        breakpoint: 0,
                        slidesToShow,
                        slidesToScroll
                    },
                    ...props.breakpoints
                ],
                windowWidth
            )
        );
    };

    const initializeSlider = () => {
        setCurrentBreakpointSettings();

        if (!isSliderLoadedRef.current) {
            setTimeout(() => {
                setIsSliderLoaded(true);
                skipToSlide(currentIndexRef.current);
            }, 0);
        }
    };

    const onResize = () => {
        setCurrentBreakpointSettings();

        const isDelayed = Number.isFinite(props.onResizeDelay);
        if (isSliderLoaded) {
            isDelayed ? setTimeout(() => goToSlide(currentIndexRef.current), props.onResizeDelay) : goToSlide(currentIndexRef.current);
        }
    };

    // attach resize events
    useEffect(() => {
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, [currentIndex, isSliderLoaded, breakpointSettings]);

    /*
     * slider logic
     */

    // init
    useEffect(() => {
        if (isMounted) {
            initializeSlider();
        }
    }, [isMounted]);

    // attach keyboard events
    useEffect(() => {
        if (!disableKeyboardEvents) {
            document.addEventListener("keydown", handleSliderKeyDown);
        }
        return () => {
            if (!disableKeyboardEvents) {
                document.removeEventListener("keydown", handleSliderKeyDown);
            }
        };
    }, []);

    // Fallback when transitionend does not fire (e.g. iOS). Clone skip has no transform transition — unlock immediately.
    useEffect(() => {
        const isSkippingSlide = preventAnimationRef.current;
        isTransitioning.current = !isSkippingSlide;

        if (isSkippingSlide) return;

        const timeout = setTimeout(() => {
            isTransitioning.current = false;
        }, speed);

        return () => {
            clearTimeout(timeout);
        };
    }, [currentIndex, speed]);

    slideCountRef.current = props.slides.length;

    const getGalleryWidth = () => {
        if (gallerySliderRef.current) return gallerySliderRef.current.clientWidth;
        return 0;
    };

    // Sync in an effect (not during render) so the ref reliably reflects committed state under React 19.
    useEffect(() => {
        disableSwipeRef.current = isSwipeDisabled;
    }, [isSwipeDisabled]);

    const onGestureEnd = (gestureDirection: GestureDirection) => {
        if (gestureDirection && !isTransitioning.current) {
            pendingSlideNavigationRef.current = SliderGallerySlideNavigation.SWIPE;
            const nextSlide = currentIndexRef.current + gestureDirection;
            goToSlide(nextSlide);
        }
    };

    const {shouldBlockEventsRef} = useSliderTouchEvents(
        galleryHolderRef,
        initializeSlider,
        onGestureEnd,
        isMobile,
        () => !!disableSwipeRef.current,
        isSwipeDisabled,
        swipeDeadband
    );

    /*
     * slide traversing methods
     */
    const setNewCurrentSlide = (slide: number) => {
        setCurrentIndex(slide);
        setTranslateValue(((clonedSlidesCount + slide) * getGalleryWidth() * -slidesToScroll) / breakpointSettingsRef.current.slidesToShow);
    };

    const skipToSlide = (slide: number) => {
        preventAnimationRef.current = true;
        setNewCurrentSlide(slide);

        scheduleAfterPaint(() => {
            preventAnimationRef.current = false;
        });
    };

    const handleSliderTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget || event.propertyName !== "transform") return;

        isTransitioning.current = false;
    };

    const goToSlide = (slide: number) => {
        const slideCount = slideCountRef.current;
        const lastSlideIndex = slideCount - 1;
        const {slidesToScroll: slidesToScrollSetting} = breakpointSettingsRef.current;

        if (slide < 0 || slide > lastSlideIndex) {
            if (preventInfinityScroll) return;

            isTransitioning.current = true;

            const slideToSkipToBeforeAnim = slide < 0 ? slideCount + currentIndexRef.current : slide - slideCount - slidesToScrollSetting;
            const nextSlide = slide < 0 ? slideToSkipToBeforeAnim - slidesToScrollSetting : slideToSkipToBeforeAnim + slidesToScrollSetting;

            skipToSlide(slideToSkipToBeforeAnim);

            scheduleAfterPaint(() => {
                goToSlide(nextSlide);
            });

            return;
        }

        if (currentIndexRef.current !== slide) {
            onSlideChange?.(currentIndexRef.current, slide, pendingSlideNavigationRef.current ?? undefined);
            pendingSlideNavigationRef.current = null;
            // Lock only on a real slide change. On resize (e.g. mobile address bar hiding on scroll)
            // goToSlide(currentIndex) runs with no transition, so nothing would ever unlock it.
            isTransitioning.current = true;
        }

        setNewCurrentSlide(slide);
    };

    /*
     * slide traversing - arrow click + keyboard event handlers
     */

    const handleSliderKeyDown = useCallback((e: Event) => {
        // casting as unknown because "keydown" event does not accept KeyDownEventHandler O_o . I gave up
        const keyboardEvent = e as unknown as KeyboardEvent;
        if (keyboardEvent.key === "ArrowLeft") slideLeft();
        else if (keyboardEvent.key === "ArrowRight") slideRight();
    }, []);

    const slideRight = useCallback(() => {
        if (shouldBlockEventsRef.current || isTransitioning.current) return;

        const newSlide = currentIndexRef.current + breakpointSettingsRef.current.slidesToScroll;
        if (preventInfinityScroll && newSlide > slideCountRef.current - 1) return;

        pendingSlideNavigationRef.current = SliderGallerySlideNavigation.ARROW_NEXT;
        goToSlide(newSlide);
    }, []);

    const handleSlideRightOnClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        slideRight();
    };

    const slideLeft = useCallback(() => {
        if (shouldBlockEventsRef.current || isTransitioning.current) return;

        const newSlide = currentIndexRef.current - breakpointSettingsRef.current.slidesToScroll;
        if (preventInfinityScroll && newSlide < 0) return;

        pendingSlideNavigationRef.current = SliderGallerySlideNavigation.ARROW_PREV;
        goToSlide(newSlide);
    }, []);

    const handleSlideLeftOnClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        slideLeft();
    };
    /*
     * misc
     */

    const preventClickEventsOnGesture: MouseEventHandler = (e) => {
        // prevent click events when gesture is detected
        if (shouldBlockEventsRef.current) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    /*
     * render
     */

    const slidesToShowDynamic = breakpointSettingsRef.current.slidesToShow;
    const transitionStyle: CSSProperties = !preventAnimationRef.current
        ? {transform: `translateX(${translateValue}px)`, transition: `transform ${speed}ms ${sliderTransition}`}
        : {transform: `translateX(${translateValue}px)`};
    const singleSlideWidth: CSSProperties = {minWidth: `${slidesToShowDynamic ? 100 / slidesToShowDynamic : 100}%`};
    const galleryHolderCN = classNames(props.className, galleryHolder);
    const sliderCN = classNames(props.className, slider);
    const singleSliderCN = classNames(props.slideStyles, singleSlide);
    const sliderWrapperCN = classNames(sliderInnerWrap, props.sliderWrapperClass);

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === props.slides.length - 1;
    const leftArrowCN = classNames(arrow, preventInfinityScroll && isFirstSlide && arrowDisabled);
    const rightArrowCN = classNames(arrow, preventInfinityScroll && isLastSlide && arrowDisabled);

    return (
        <div ref={galleryHolderRef} onClick={preventClickEventsOnGesture} className={galleryHolderCN}>
            {props.arrows && props.slides.length > 1 && (
                <span onClick={handleSlideLeftOnClick} className={leftArrowCN}>
                    {props.arrows.left}
                </span>
            )}
            <div className={sliderWrapperCN}>
                {!isSliderLoaded ? (
                    <div ref={gallerySliderRef} className={sliderCN} style={transitionStyle}>
                        <div key={clonedSlidesCount} className={singleSliderCN} style={singleSlideWidth}>
                            {props.slides[initialSlide]}
                        </div>
                    </div>
                ) : (
                    <div ref={gallerySliderRef} className={sliderCN} style={transitionStyle} onTransitionEnd={handleSliderTransitionEnd}>
                        {slidesListWithClones.map((slide, index) => (
                            <div key={isSliderLoaded ? index + clonedSlidesCount : index} className={singleSliderCN} style={singleSlideWidth}>
                                {slide}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {props.arrows && props.slides.length > 1 && (
                <span onClick={handleSlideRightOnClick} className={rightArrowCN}>
                    {props.arrows.right}
                </span>
            )}
        </div>
    );
};
import React, {CSSProperties, FC, KeyboardEvent, MouseEventHandler, ReactNode, useCallback, useEffect, useRef, useState} from "react";
import classNames from "classnames";

import {useIsMobile, useIsMounted, useStateRef} from "@pg-mono/hooks";

import {GestureDirection, useSliderTouchEvents} from "../hooks/use_slider_touch_events";
import {SliderGallerySlideNavigation} from "../types/SliderGallerySlideNavigation";
import {getClosestBreakpoint} from "../utils/get_closest_breakpoint";

import {arrow, arrowDisabled, galleryHolder, singleSlide, slider, sliderInnerWrap} from "./SliderGallery.module.css";

export type ISliderTransition = "ease" | "linear" | "ease-in" | "ease-out" | "ease-in-out";
export type IBreakpointSettings = {breakpoint: number; slidesToShow: number; slidesToScroll: number};

interface IProps {
    slides: ReactNode[];
    arrows?: {
        left: React.JSX.Element;
        right: React.JSX.Element;
    };
    preventLazyLoad?: boolean;
    onSlideChange?: (currentSlideIndex: number, nextSlideIndex: number, navigation?: SliderGallerySlideNavigation) => void;
    sliderTransition?: ISliderTransition;
    clonedSlidesCount?: number;
    forceInitialize?: true;
    onResizeDelay?: number; // milliseconds
    initialSlide?: number;
    disableKeyboardEvents?: boolean;
    className?: string;
    sliderWrapperClass?: string;
    slideStyles?: CSSProperties | CSSProperties[];
    preventInfinityScroll?: boolean;
    disableSwipe?: boolean;
    swipeDeadband?: number;

    /// slick-slider-like options:
    slidesToShow?: number;
    slidesToScroll?: number;
    speed?: number;
    breakpoints?: IBreakpointSettings[];
}

const DEFAULT_SPEED = 300;
const DEFAULT_SLIDER_TRANSITION = "ease-out";
const DEFAULT_CLONED_SLIDES_COUNT = 2;
const DEFAULT_SLIDES_TO_SHOW = 1;
const DEFAULT_SLIDES_TO_SCROLL = 1;

// Wait for paint before the next transform step (e.g. instant clone skip, then animated wrap).
const scheduleAfterPaint = (callback: () => void) => {
    requestAnimationFrame(() => {
        requestAnimationFrame(callback);
    });
};

// I am using a lot of refs here to avoid potential issues with stale state being used inside event listeners. Reference to equal state value will always be up to date.

export const SliderGallery: FC<IProps> = (props) => {
    const {
        sliderTransition = DEFAULT_SLIDER_TRANSITION,
        speed = DEFAULT_SPEED,
        clonedSlidesCount = DEFAULT_CLONED_SLIDES_COUNT,
        initialSlide = 0,
        slidesToShow = DEFAULT_SLIDES_TO_SHOW,
        slidesToScroll = DEFAULT_SLIDES_TO_SCROLL,
        disableKeyboardEvents,
        preventInfinityScroll = clonedSlidesCount === 0,
        disableSwipe,
        swipeDeadband,
        onSlideChange
    } = props;

    const galleryHolderRef = useRef<HTMLDivElement | null>(null);
    const gallerySliderRef = useRef<HTMLDivElement | null>(null);
    const isMobile = useIsMobile(true);
    const isMounted = useIsMounted();
    const isSwipeDisabled = Boolean(disableSwipe) || props.slides.length <= 1;

    const [isSliderLoaded, setIsSliderLoaded, isSliderLoadedRef] = useStateRef(false);
    const [translateValue, setTranslateValue] = useState(0);
    const [currentIndex, setCurrentIndex, currentIndexRef] = useStateRef(initialSlide);
    const [breakpointSettings, setBreakpointSettings, breakpointSettingsRef] = useStateRef({
        breakpoint: 0,
        slidesToShow,
        slidesToScroll
    });

    const isTransitioning = useRef(false);
    const slideCountRef = useRef(0);
    const preventAnimationRef = useRef(false);
    const pendingSlideNavigationRef = useRef<SliderGallerySlideNavigation | null>(null);
    const disableSwipeRef = useRef(isSwipeDisabled);

    const slidesListWithClones =
        isSliderLoaded && clonedSlidesCount > 0
            ? [...props.slides.slice(-1 * clonedSlidesCount), ...props.slides, ...props.slides.slice(0, clonedSlidesCount)]
            : props.slides;

    /*
     * initialization, breakpoints and resizing logic
     */

    const setCurrentBreakpointSettings = () => {
        if (!props.breakpoints) return;

        const windowWidth = window.innerWidth;
        setBreakpointSettings(
            getClosestBreakpoint(
                [
                    {
                        breakpoint: 0,
                        slidesToShow,
                        slidesToScroll
                    },
                    ...props.breakpoints
                ],
                windowWidth
            )
        );
    };

    const initializeSlider = () => {
        setCurrentBreakpointSettings();

        if (!isSliderLoadedRef.current) {
            setTimeout(() => {
                setIsSliderLoaded(true);
                skipToSlide(currentIndexRef.current);
            }, 0);
        }
    };

    const onResize = () => {
        setCurrentBreakpointSettings();

        const isDelayed = Number.isFinite(props.onResizeDelay);
        if (isSliderLoaded) {
            isDelayed ? setTimeout(() => goToSlide(currentIndexRef.current), props.onResizeDelay) : goToSlide(currentIndexRef.current);
        }
    };

    // attach resize events
    useEffect(() => {
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, [currentIndex, isSliderLoaded, breakpointSettings]);

    /*
     * slider logic
     */

    // init
    useEffect(() => {
        if (isMounted) {
            initializeSlider();
        }
    }, [isMounted]);

    // attach keyboard events
    useEffect(() => {
        if (!disableKeyboardEvents) {
            document.addEventListener("keydown", handleSliderKeyDown);
        }
        return () => {
            if (!disableKeyboardEvents) {
                document.removeEventListener("keydown", handleSliderKeyDown);
            }
        };
    }, []);

    // Fallback when transitionend does not fire (e.g. iOS). Clone skip has no transform transition — unlock immediately.
    useEffect(() => {
        const isSkippingSlide = preventAnimationRef.current;
        isTransitioning.current = !isSkippingSlide;

        if (isSkippingSlide) return;

        const timeout = setTimeout(() => {
            isTransitioning.current = false;
        }, speed);

        return () => {
            clearTimeout(timeout);
        };
    }, [currentIndex, speed]);

    slideCountRef.current = props.slides.length;

    const getGalleryWidth = () => {
        if (gallerySliderRef.current) return gallerySliderRef.current.clientWidth;
        return 0;
    };

    // Sync in an effect (not during render) so the ref reliably reflects committed state under React 19.
    useEffect(() => {
        disableSwipeRef.current = isSwipeDisabled;
    }, [isSwipeDisabled]);

    const onGestureEnd = (gestureDirection: GestureDirection) => {
        if (gestureDirection && !isTransitioning.current) {
            pendingSlideNavigationRef.current = SliderGallerySlideNavigation.SWIPE;
            const nextSlide = currentIndexRef.current + gestureDirection;
            goToSlide(nextSlide);
        }
    };

    const {shouldBlockEventsRef} = useSliderTouchEvents(
        galleryHolderRef,
        initializeSlider,
        onGestureEnd,
        isMobile,
        () => !!disableSwipeRef.current,
        isSwipeDisabled,
        swipeDeadband
    );

    /*
     * slide traversing methods
     */
    const setNewCurrentSlide = (slide: number) => {
        setCurrentIndex(slide);
        setTranslateValue(((clonedSlidesCount + slide) * getGalleryWidth() * -slidesToScroll) / breakpointSettingsRef.current.slidesToShow);
    };

    const skipToSlide = (slide: number) => {
        preventAnimationRef.current = true;
        setNewCurrentSlide(slide);

        scheduleAfterPaint(() => {
            preventAnimationRef.current = false;
        });
    };

    const handleSliderTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget || event.propertyName !== "transform") return;

        isTransitioning.current = false;
    };

    const goToSlide = (slide: number) => {
        const slideCount = slideCountRef.current;
        const lastSlideIndex = slideCount - 1;
        const {slidesToScroll: slidesToScrollSetting} = breakpointSettingsRef.current;

        if (slide < 0 || slide > lastSlideIndex) {
            if (preventInfinityScroll) return;

            isTransitioning.current = true;

            const slideToSkipToBeforeAnim = slide < 0 ? slideCount + currentIndexRef.current : slide - slideCount - slidesToScrollSetting;
            const nextSlide = slide < 0 ? slideToSkipToBeforeAnim - slidesToScrollSetting : slideToSkipToBeforeAnim + slidesToScrollSetting;

            skipToSlide(slideToSkipToBeforeAnim);

            scheduleAfterPaint(() => {
                goToSlide(nextSlide);
            });

            return;
        }

        if (currentIndexRef.current !== slide) {
            onSlideChange?.(currentIndexRef.current, slide, pendingSlideNavigationRef.current ?? undefined);
            pendingSlideNavigationRef.current = null;
            // Lock only on a real slide change. On resize (e.g. mobile address bar hiding on scroll)
            // goToSlide(currentIndex) runs with no transition, so nothing would ever unlock it.
            isTransitioning.current = true;
        }

        setNewCurrentSlide(slide);
    };

    /*
     * slide traversing - arrow click + keyboard event handlers
     */

    const handleSliderKeyDown = useCallback((e: Event) => {
        // casting as unknown because "keydown" event does not accept KeyDownEventHandler O_o . I gave up
        const keyboardEvent = e as unknown as KeyboardEvent;
        if (keyboardEvent.key === "ArrowLeft") slideLeft();
        else if (keyboardEvent.key === "ArrowRight") slideRight();
    }, []);

    const slideRight = useCallback(() => {
        if (shouldBlockEventsRef.current || isTransitioning.current) return;

        const newSlide = currentIndexRef.current + breakpointSettingsRef.current.slidesToScroll;
        if (preventInfinityScroll && newSlide > slideCountRef.current - 1) return;

        pendingSlideNavigationRef.current = SliderGallerySlideNavigation.ARROW_NEXT;
        goToSlide(newSlide);
    }, []);

    const handleSlideRightOnClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        slideRight();
    };

    const slideLeft = useCallback(() => {
        if (shouldBlockEventsRef.current || isTransitioning.current) return;

        const newSlide = currentIndexRef.current - breakpointSettingsRef.current.slidesToScroll;
        if (preventInfinityScroll && newSlide < 0) return;

        pendingSlideNavigationRef.current = SliderGallerySlideNavigation.ARROW_PREV;
        goToSlide(newSlide);
    }, []);

    const handleSlideLeftOnClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        slideLeft();
    };
    /*
     * misc
     */

    const preventClickEventsOnGesture: MouseEventHandler = (e) => {
        // prevent click events when gesture is detected
        if (shouldBlockEventsRef.current) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    /*
     * render
     */

    const slidesToShowDynamic = breakpointSettingsRef.current.slidesToShow;
    const transitionStyle: CSSProperties = !preventAnimationRef.current
        ? {transform: `translateX(${translateValue}px)`, transition: `transform ${speed}ms ${sliderTransition}`}
        : {transform: `translateX(${translateValue}px)`};
    const singleSlideWidth: CSSProperties = {minWidth: `${slidesToShowDynamic ? 100 / slidesToShowDynamic : 100}%`};
    const galleryHolderCN = classNames(props.className, galleryHolder);
    const sliderCN = classNames(props.className, slider);
    const singleSliderCN = classNames(props.slideStyles, singleSlide);
    const sliderWrapperCN = classNames(sliderInnerWrap, props.sliderWrapperClass);

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === props.slides.length - 1;
    const leftArrowCN = classNames(arrow, preventInfinityScroll && isFirstSlide && arrowDisabled);
    const rightArrowCN = classNames(arrow, preventInfinityScroll && isLastSlide && arrowDisabled);

    return (
        <div ref={galleryHolderRef} onClick={preventClickEventsOnGesture} className={galleryHolderCN}>
            {props.arrows && props.slides.length > 1 && (
                <span onClick={handleSlideLeftOnClick} className={leftArrowCN}>
                    {props.arrows.left}
                </span>
            )}
            <div className={sliderWrapperCN}>
                {!isSliderLoaded ? (
                    <div ref={gallerySliderRef} className={sliderCN} style={transitionStyle}>
                        <div key={clonedSlidesCount} className={singleSliderCN} style={singleSlideWidth}>
                            {props.slides[initialSlide]}
                        </div>
                    </div>
                ) : (
                    <div ref={gallerySliderRef} className={sliderCN} style={transitionStyle} onTransitionEnd={handleSliderTransitionEnd}>
                        {slidesListWithClones.map((slide, index) => (
                            <div key={isSliderLoaded ? index + clonedSlidesCount : index} className={singleSliderCN} style={singleSlideWidth}>
                                {slide}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {props.arrows && props.slides.length > 1 && (
                <span onClick={handleSlideRightOnClick} className={rightArrowCN}>
                    {props.arrows.right}
                </span>
            )}
        </div>
    );
};
